import { useState, useEffect, useContext, useCallback } from "react";
import { RotateCcw, Split } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";

// Card Component - Texas Hold'em style
const Card = ({ card, hidden = false, size = "normal" }) => {
  const sizeClasses = {
    small: "w-10 h-14 text-xs",
    normal: "w-14 h-20 text-sm",
    large: "w-16 h-24 text-base"
  };

  if (!card || hidden) {
    return (
      <div className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600 shadow-lg flex items-center justify-center`}
        style={{ 
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.03) 5px, rgba(255,255,255,0.03) 10px)'
        }}
      >
        <div className="text-blue-400 opacity-50 text-xl">♠</div>
      </div>
    );
  }

  const suits = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
  const suitSymbol = suits[card.suit] || card.suit;
  const isRed = card.suit === 'hearts' || card.suit === 'diamonds';

  return (
    <div className={`${sizeClasses[size]} rounded-lg bg-white border-2 border-gray-300 shadow-lg flex flex-col items-center justify-center font-bold ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
      <span>{card.rank}</span>
      <span className="text-lg">{suitSymbol}</span>
    </div>
  );
};

// Calculate hand value
const calculateHandValue = (cards) => {
  let value = 0;
  let aces = 0;

  for (const card of cards) {
    if (card.rank === 'A') {
      aces += 1;
      value += 11;
    } else if (['K', 'Q', 'J'].includes(card.rank)) {
      value += 10;
    } else {
      value += parseInt(card.rank);
    }
  }

  while (value > 21 && aces > 0) {
    value -= 10;
    aces -= 1;
  }

  return value;
};

// Check if two cards can be split (same rank)
const canSplit = (cards) => {
  if (!cards || cards.length !== 2) return false;
  return cards[0].rank === cards[1].rank;
};

// Create a deck
const createDeck = () => {
  const suits = ['hearts', 'diamonds', 'clubs', 'spades'];
  const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  const deck = [];

  for (const suit of suits) {
    for (const rank of ranks) {
      deck.push({ suit, rank });
    }
  }

  // Shuffle
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }

  return deck;
};

const BlackjackGame = ({ API, token, onClose }) => {
  const { user } = useContext(AuthContext);
  const [gamePhase, setGamePhase] = useState('betting');
  const [deck, setDeck] = useState([]);
  const [playerHands, setPlayerHands] = useState([[]]); // Array of hands for split
  const [activeHandIndex, setActiveHandIndex] = useState(0);
  const [dealerHand, setDealerHand] = useState([]);
  const [bet, setBet] = useState(0);
  const [handBets, setHandBets] = useState([0]); // Bet per hand
  const [chips, setChips] = useState(1000);
  const [message, setMessage] = useState('Place your bet!');
  const [results, setResults] = useState([]); // Result per hand
  const [dealerHidden, setDealerHidden] = useState(true);
  const [hasSplit, setHasSplit] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await axios.get(`${API}/fchips/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChips(res.data.balance || 1000);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    fetchBalance();
  }, [API, token]);

  const dealCards = () => {
    if (bet <= 0) {
      toast.error("Place a bet first!");
      return;
    }
    if (bet > chips) {
      toast.error("Not enough chips!");
      return;
    }

    const newDeck = createDeck();
    const pHand = [newDeck.pop(), newDeck.pop()];
    const dHand = [newDeck.pop(), newDeck.pop()];

    setDeck(newDeck);
    setPlayerHands([pHand]);
    setActiveHandIndex(0);
    setDealerHand(dHand);
    setDealerHidden(true);
    setGamePhase('playing');
    setResults([]);
    setHandBets([bet]);
    setHasSplit(false);

    const playerValue = calculateHandValue(pHand);
    const dealerValue = calculateHandValue(dHand);

    if (playerValue === 21) {
      if (dealerValue === 21) {
        endAllHands([{ type: 'push', message: "Both Blackjack! Push." }]);
      } else {
        endAllHands([{ type: 'blackjack', message: "BLACKJACK! 1.5x!" }]);
      }
    } else {
      setMessage(canSplit(pHand) ? "Hit, Stand, or Split?" : "Hit or Stand?");
    }
  };

  const splitHand = () => {
    if (!canSplit(playerHands[activeHandIndex])) return;
    if (bet > chips) {
      toast.error("Not enough chips to split!");
      return;
    }

    const currentHand = playerHands[activeHandIndex];
    const newDeck = [...deck];
    
    // Split the cards into two hands, deal one card to each
    const hand1 = [currentHand[0], newDeck.pop()];
    const hand2 = [currentHand[1], newDeck.pop()];
    
    const newHands = [...playerHands];
    newHands[activeHandIndex] = hand1;
    newHands.push(hand2);
    
    setDeck(newDeck);
    setPlayerHands(newHands);
    setHandBets(prev => [...prev, bet]);
    setChips(prev => prev - bet); // Deduct extra bet for split
    setHasSplit(true);
    setMessage("Playing Hand 1 - Hit or Stand?");
    toast.success("Cards split! Playing first hand.");
  };

  const hit = () => {
    if (gamePhase !== 'playing') return;

    const newDeck = [...deck];
    const newCard = newDeck.pop();
    const newHands = [...playerHands];
    newHands[activeHandIndex] = [...newHands[activeHandIndex], newCard];
    
    setDeck(newDeck);
    setPlayerHands(newHands);

    const value = calculateHandValue(newHands[activeHandIndex]);
    if (value > 21) {
      // Bust this hand
      handleHandComplete(activeHandIndex, 'bust');
    } else if (value === 21) {
      setMessage("21! Standing automatically.");
      setTimeout(() => stand(), 500);
    }
  };

  const handleHandComplete = (handIndex, outcome) => {
    const newResults = [...results];
    newResults[handIndex] = outcome;
    setResults(newResults);

    // Move to next hand or dealer's turn
    if (handIndex < playerHands.length - 1) {
      setActiveHandIndex(handIndex + 1);
      setMessage(`Playing Hand ${handIndex + 2} - Hit or Stand?`);
    } else {
      // All hands complete, dealer's turn
      playDealerTurn();
    }
  };

  const stand = () => {
    if (gamePhase !== 'playing') return;
    
    const currentValue = calculateHandValue(playerHands[activeHandIndex]);
    
    if (activeHandIndex < playerHands.length - 1) {
      // More hands to play
      const newResults = [...results];
      newResults[activeHandIndex] = currentValue > 21 ? 'bust' : 'stand';
      setResults(newResults);
      setActiveHandIndex(activeHandIndex + 1);
      setMessage(`Playing Hand ${activeHandIndex + 2} - Hit or Stand?`);
    } else {
      // Last hand, dealer plays
      playDealerTurn();
    }
  };

  const playDealerTurn = () => {
    setGamePhase('dealerTurn');
    setDealerHidden(false);
    setMessage("Dealer's turn...");

    let dHand = [...dealerHand];
    let currentDeck = [...deck];

    const dealerPlay = () => {
      let dealerValue = calculateHandValue(dHand);

      if (dealerValue < 17) {
        const newCard = currentDeck.pop();
        dHand = [...dHand, newCard];
        setDealerHand(dHand);
        setDeck([...currentDeck]);
        setTimeout(dealerPlay, 800);
      } else {
        // Dealer done, resolve all hands
        resolveAllHands(dealerValue, dHand);
      }
    };

    setTimeout(dealerPlay, 500);
  };

  const resolveAllHands = (dealerValue, finalDealerHand) => {
    const dealerBust = dealerValue > 21;
    const finalResults = playerHands.map((hand, i) => {
      const playerValue = calculateHandValue(hand);
      
      if (playerValue > 21) return { type: 'lose', message: 'BUST' };
      if (dealerBust) return { type: 'win', message: 'Dealer busts!' };
      if (playerValue > dealerValue) return { type: 'win', message: 'You win!' };
      if (playerValue < dealerValue) return { type: 'lose', message: 'Dealer wins' };
      return { type: 'push', message: 'Push' };
    });

    endAllHands(finalResults);
  };

  const endAllHands = (finalResults) => {
    setGamePhase('gameOver');
    setDealerHidden(false);
    setResults(finalResults);

    let totalWinnings = 0;
    finalResults.forEach((result, i) => {
      const handBet = handBets[i] || bet;
      if (result.type === 'blackjack') {
        totalWinnings += handBet + Math.floor(handBet * 1.5);
      } else if (result.type === 'win') {
        totalWinnings += handBet * 2;
      } else if (result.type === 'push') {
        totalWinnings += handBet;
      }
      // lose = 0 winnings
    });

    if (totalWinnings > 0) {
      setChips(prev => prev + totalWinnings);
    }

    const totalBet = handBets.reduce((a, b) => a + b, 0);
    const netResult = totalWinnings - totalBet;
    setMessage(netResult > 0 ? `Won ${netResult}!` : netResult < 0 ? `Lost ${-netResult}` : 'Push!');
  };

  const placeBet = (amount) => {
    if (gamePhase !== 'betting') return;
    if (amount > chips) {
      toast.error("Not enough chips!");
      return;
    }
    setBet(amount);
    setChips(prev => prev - amount);
    setMessage(`Bet: ${amount} chips. Click DEAL!`);
  };

  const newHand = () => {
    setGamePhase('betting');
    setBet(0);
    setPlayerHands([[]]);
    setActiveHandIndex(0);
    setDealerHand([]);
    setResults([]);
    setHandBets([0]);
    setDealerHidden(true);
    setHasSplit(false);
    setMessage('Place your bet!');
  };

  const activeHand = playerHands[activeHandIndex] || [];
  const canSplitNow = gamePhase === 'playing' && !hasSplit && canSplit(activeHand) && chips >= bet;

  return (
    <div className="w-full h-full min-h-[450px] flex flex-col">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-gray-900/90 to-gray-800/90 px-4 py-2 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-yellow-400 font-bold">🃏 BLACKJACK 21</span>
          {hasSplit && <span className="text-purple-400 text-xs">(Split Active)</span>}
        </div>
        <div className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded">
          <span className="text-yellow-400 text-sm">💰</span>
          <span className="text-white font-bold text-sm">{chips.toLocaleString()}</span>
        </div>
      </div>

      {/* THE TABLE - Oval green table */}
      <div className="flex-1 relative bg-gray-800 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[85%] rounded-[50%] bg-gradient-to-b from-green-700 to-green-900 border-8 border-amber-700 shadow-[0_0_40px_rgba(0,0,0,0.5),inset_0_0_40px_rgba(0,0,0,0.3)]">
          <div className="absolute inset-4 border-2 border-amber-600/30 rounded-[50%]" />
          
          {/* Dealer Area */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2">
            <div className="bg-gradient-to-b from-yellow-600 to-yellow-800 rounded-lg px-3 py-1 border-2 border-yellow-400 mb-2 mx-auto w-fit">
              <div className="flex items-center gap-2">
                <span className="text-lg">👩‍💼</span>
                <span className="text-white text-xs font-bold">DEALER</span>
              </div>
            </div>
            <div className="flex gap-1 justify-center">
              {dealerHand.length > 0 ? dealerHand.map((card, i) => (
                <Card key={i} card={card} hidden={dealerHidden && i === 1} size="normal" />
              )) : (
                <>
                  <Card hidden size="normal" />
                  <Card hidden size="normal" />
                </>
              )}
            </div>
            {!dealerHidden && dealerHand.length > 0 && (
              <div className="text-white text-xs font-bold text-center mt-1 bg-black/50 rounded px-2">
                {calculateHandValue(dealerHand)}
              </div>
            )}
          </div>

          {/* Center Message & Results */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            {results.length > 0 && gamePhase === 'gameOver' ? (
              <div className="space-y-2">
                {results.map((r, i) => (
                  <div key={i} className={`text-lg font-black px-4 py-2 rounded-xl shadow-xl ${
                    r.type === 'win' || r.type === 'blackjack' 
                      ? 'bg-gradient-to-r from-green-600 to-green-500 text-white' 
                      : r.type === 'lose' 
                        ? 'bg-gradient-to-r from-red-600 to-red-500 text-white' 
                        : 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-white'
                  }`}>
                    {hasSplit && `Hand ${i + 1}: `}
                    {r.type === 'blackjack' ? '🎰 BLACKJACK!' :
                     r.type === 'win' ? '🎉 WIN!' :
                     r.type === 'lose' ? '😢 LOSE' :
                     '🤝 PUSH'}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-white text-sm font-bold bg-black/60 px-4 py-2 rounded-full">
                {message}
              </div>
            )}
            {bet > 0 && (
              <div className="mt-2 bg-black/50 rounded-full px-3 py-1">
                <span className="text-yellow-400 text-xs font-bold">
                  {hasSplit ? `TOTAL BET: ${handBets.reduce((a, b) => a + b, 0)}` : `BET: ${bet}`}
                </span>
              </div>
            )}
          </div>

          {/* Player Hands */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
            <div className={`flex ${hasSplit ? 'gap-8' : 'gap-1'} justify-center`}>
              {playerHands.map((hand, handIdx) => (
                <div key={handIdx} className={`flex flex-col items-center ${handIdx === activeHandIndex && gamePhase === 'playing' ? 'ring-2 ring-yellow-400 rounded-lg p-2' : ''}`}>
                  {hasSplit && (
                    <div className={`text-xs font-bold mb-1 ${handIdx === activeHandIndex ? 'text-yellow-400' : 'text-gray-400'}`}>
                      Hand {handIdx + 1}
                    </div>
                  )}
                  <div className="flex gap-1">
                    {hand.length > 0 ? hand.map((card, i) => (
                      <Card key={i} card={card} size={hasSplit ? "small" : "normal"} />
                    )) : (
                      <>
                        <Card hidden size="normal" />
                        <Card hidden size="normal" />
                      </>
                    )}
                  </div>
                  {hand.length > 0 && (
                    <div className={`text-xs font-bold mt-1 px-2 rounded ${
                      calculateHandValue(hand) > 21 ? 'bg-red-500 text-white' : 'bg-black/50 text-white'
                    }`}>
                      {calculateHandValue(hand)}{calculateHandValue(hand) > 21 ? ' BUST' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Player Info */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="w-10 h-10 rounded-full ring-2 ring-green-400 bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center shadow-xl">
                <span className="text-lg">👤</span>
              </div>
              <div className="text-center">
                <div className="text-green-400 text-xs font-bold">★ YOU ★</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 border-t border-gray-700">
        {gamePhase === 'betting' && (
          <div className="space-y-2">
            <div className="text-gray-400 text-xs text-center">Choose your bet:</div>
            <div className="flex gap-2 justify-center flex-wrap">
              {[10, 25, 50, 100, 250, 500].map(amount => (
                <button
                  key={amount}
                  onClick={() => placeBet(amount)}
                  disabled={amount > chips}
                  className={`px-3 py-1.5 rounded font-bold text-xs ${
                    amount > chips
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : bet === amount
                      ? 'bg-yellow-500 text-black ring-2 ring-yellow-300'
                      : 'bg-gradient-to-b from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 text-white'
                  }`}
                >
                  {amount}
                </button>
              ))}
            </div>
            {bet > 0 && (
              <button
                onClick={dealCards}
                className="w-full py-2 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-lg text-white font-bold"
              >
                🃏 DEAL
              </button>
            )}
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="flex gap-2 flex-wrap justify-center">
            <button
              onClick={hit}
              className="flex-1 max-w-[120px] py-2 bg-gradient-to-b from-blue-600 to-blue-800 hover:from-blue-500 hover:to-blue-700 rounded-lg text-white font-bold border border-blue-500"
            >
              HIT
            </button>
            <button
              onClick={stand}
              className="flex-1 max-w-[120px] py-2 bg-gradient-to-b from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 rounded-lg text-white font-bold border border-red-500"
            >
              STAND
            </button>
            {canSplitNow && (
              <button
                onClick={splitHand}
                className="flex-1 max-w-[120px] py-2 bg-gradient-to-b from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 rounded-lg text-white font-bold border border-purple-500 flex items-center justify-center gap-1"
              >
                <Split className="w-4 h-4" /> SPLIT
              </button>
            )}
          </div>
        )}

        {gamePhase === 'dealerTurn' && (
          <div className="text-center text-yellow-400 font-bold py-2 animate-pulse">
            Dealer is playing...
          </div>
        )}

        {gamePhase === 'gameOver' && (
          <button
            onClick={newHand}
            className="w-full py-2 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-lg text-white font-bold flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> NEW HAND
          </button>
        )}
      </div>
    </div>
  );
};

export default BlackjackGame;
